// RiskLadderModal full-body port — implements the port-spec at
// immersive_webapp/reports/exotics_riskladder_port.md verbatim.
//
// Structure (top to bottom):
//   1. Header with token/contract/expiry/model badge + close ×
//   2. Metric ToggleButtonGroup (PV / DELTA / GAMMA / THETA / VEGA),
//      active button colours by metric, selection persisted to
//      localStorage per-instrument
//   3. Readout row: spot value + metric-at-spot value
//   4. ECharts ladder chart — line + markers, dashed zero line,
//      dashed current-spot vertical line, ECharts handles ticks
//   5. Bottom row: X-axis ToggleButtonGroup (Spot $ / Shock %) +
//      Copy CSV button
//
// Loading / error / no-data branches all render through the shared
// <EmptyState/> primitive.
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faCopy } from '@fortawesome/free-solid-svg-icons';

import Chart from '../../chart';
import EmptyState from '../../empty-state';
import { greekColour, brandColour } from '../exotics-shared';
import { LadderPayload, PricingContext } from '../../../../providers/exotics-pricing-provider';

import './exotics-risk-ladder-modal-styles.scss';

type Metric = 'PV' | 'DELTA' | 'GAMMA' | 'THETA' | 'VEGA';
const METRICS: Metric[] = ['PV', 'DELTA', 'GAMMA', 'THETA', 'VEGA'];

// Per-metric decimal-place rules from port-spec §9. Matches
// RiskLadderModal.jsx:19-26.
const placesFor = (metric: Metric, abs: number): number => {
  if (metric === 'PV') return abs >= 1000 ? 0 : abs >= 10 ? 2 : 4;
  if (metric === 'DELTA') return abs >= 1000 ? 0 : abs >= 1 ? 3 : 4;
  if (metric === 'GAMMA') return abs >= 1000 ? 0 : abs >= 1 ? 3 : 5;
  if (metric === 'THETA') return abs >= 1000 ? 0 : abs >= 10 ? 2 : 3;
  if (metric === 'VEGA') return abs >= 1000 ? 0 : abs >= 10 ? 2 : 3;
  return 2;
};

const fmtNumber = (v: number, places: number): string =>
  v.toLocaleString(undefined, { minimumFractionDigits: places, maximumFractionDigits: places });

const fmtMetric = (metric: Metric, v: number | null | undefined): string => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  // Exact zero renders as "0" so axis tick labels at the zero baseline don't
  // bloat to "0.0000" — they sit on a grid of round-number values.
  if (v === 0) return '0';
  return fmtNumber(v, placesFor(metric, Math.abs(v)));
};

const fmtSpot = (v: number | null | undefined): string => {
  if (v === null || v === undefined) return '—';
  if (v === 0) return '0';
  const a = Math.abs(v);
  if (a >= 1000) return fmtNumber(v, 0);
  if (a >= 10) return fmtNumber(v, 2);
  if (a >= 1) return fmtNumber(v, 3);
  return fmtNumber(v, 4);
};

// Find the metric at SHOCK=0 — the current-spot value.
const valueAtSpot = (ladder: LadderPayload | undefined, metric: Metric): number | null => {
  if (!ladder?.SHOCK) return null;
  const idx = ladder.SHOCK.findIndex((s) => Math.abs(s) < 1e-9);
  if (idx < 0) return null;
  const arr = ladder[metric];
  return arr?.[idx] ?? null;
};

const ExoticsRiskLadderModalContent: FC = () => {
  const { ladderFor, closeLadder, instruments, ladderCache, ladderLoading, errors, results } =
    useContext(PricingContext);
  const theme = useTheme();

  const open = ladderFor !== null;
  const inst = ladderFor ? instruments.find((i) => i.id === ladderFor) : null;
  const cached = ladderFor ? ladderCache[ladderFor] : null;
  const isLoading = ladderFor ? !!ladderLoading[ladderFor] : false;
  const err = ladderFor ? errors[ladderFor] : '';
  const spotAtCalc = ladderFor ? results[ladderFor]?.spot : null;

  // Metric selection persisted per-instrument
  const storageKey = ladderFor ? `diletta_ladder_metric_${ladderFor}` : null;
  const [metric, setMetric] = useState<Metric>(() => {
    if (typeof window === 'undefined' || !storageKey) return 'PV';
    try {
      const stored = window.localStorage.getItem(storageKey);
      if (stored && (METRICS as string[]).includes(stored)) return stored as Metric;
    } catch {
      /* noop */
    }
    return 'PV';
  });

  // Sync metric to localStorage on change
  useEffect(() => {
    if (!storageKey || typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(storageKey, metric);
    } catch {
      /* noop */
    }
  }, [metric, storageKey]);

  // X-axis: spot ($) or shock (%)
  const [xAxis, setXAxis] = useState<'spot' | 'shock'>('spot');

  // Draggable paper. Offset is applied via transform on PaperProps.sx so the
  // user can shove the modal aside to compare against the column behind. The
  // title bar is the drag handle; clicks on the close button are excluded
  // (we check the event target before starting a drag).
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const dragStartRef = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  // Reset position when the modal reopens so each open starts centered.
  useEffect(() => {
    if (open) setDragOffset({ x: 0, y: 0 });
  }, [open]);

  const onTitlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // Don't drag if the user clicked a button (close icon, etc.).
      const target = e.target as HTMLElement | null;
      if (target?.closest('button')) return;
      e.preventDefault();
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        ox: dragOffset.x,
        oy: dragOffset.y,
      };
      const onMove = (ev: PointerEvent) => {
        const start = dragStartRef.current;
        if (!start) return;
        setDragOffset({
          x: start.ox + (ev.clientX - start.x),
          y: start.oy + (ev.clientY - start.y),
        });
      };
      const onUp = () => {
        dragStartRef.current = null;
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [dragOffset.x, dragOffset.y],
  );

  const valueNow = useMemo(() => valueAtSpot(cached ?? undefined, metric), [cached, metric]);

  const colourForMetric = useMemo(() => {
    switch (metric) {
      case 'PV':
        return theme.palette.success.main;
      case 'DELTA':
        return greekColour(theme, 'delta');
      case 'GAMMA':
        return theme.palette.text.primary;
      case 'THETA':
        return theme.palette.error.main;
      case 'VEGA':
        return greekColour(theme, 'vega');
    }
  }, [metric, theme]);

  // Build ECharts series from cached ladder
  const series = useMemo(() => {
    if (!cached) return null;
    const xs = xAxis === 'shock' ? cached.SHOCK : cached.SPOT;
    const ys = cached[metric];
    if (!Array.isArray(xs) || !Array.isArray(ys) || xs.length === 0) return null;
    const data = xs.map((x, i) => [x, ys[i]] as [number, number]);
    return [
      {
        type: 'line',
        smooth: false,
        symbol: 'circle',
        symbolSize: 5,
        showSymbol: true,
        lineStyle: { width: 1.5, color: colourForMetric },
        itemStyle: { color: colourForMetric },
        data,
        markLine:
          spotAtCalc != null && xAxis === 'spot'
            ? {
                silent: true,
                symbol: 'none',
                lineStyle: {
                  color: theme.palette.primary.main,
                  type: 'dashed',
                  width: 1,
                  opacity: 0.7,
                },
                label: { show: false },
                data: [{ xAxis: spotAtCalc }],
              }
            : undefined,
      },
    ];
  }, [cached, metric, xAxis, colourForMetric, spotAtCalc, theme]);

  const yBounds = useMemo(() => {
    if (!series) return undefined;
    const ys = (series[0].data as Array<[number, number]>).map((p) => p[1]);
    if (!ys.length) return undefined;
    const min = Math.min(...ys);
    const max = Math.max(...ys);
    const range = max - min || Math.abs(max) || 1;
    return { min: min - range * 0.08, max: max + range * 0.08 };
  }, [series]);

  // Wrap the x-axis tightly around the data so a ladder running 70k → 88k
  // doesn't render against an axis that starts at zero. Round outward to a
  // nice step so tick labels still land on tidy values (5k, 10k etc).
  const xBounds = useMemo(() => {
    if (!series) return undefined;
    const xs = (series[0].data as Array<[number, number]>).map((p) => p[0]);
    if (!xs.length) return undefined;
    let lo = Math.min(...xs);
    let hi = Math.max(...xs);
    // Include the current-spot reference inside the visible window when
    // viewing in Spot $ mode — markLine isn't auto-included by ECharts'
    // axis-fit logic.
    if (xAxis === 'spot' && spotAtCalc != null) {
      lo = Math.min(lo, spotAtCalc);
      hi = Math.max(hi, spotAtCalc);
    }
    const span = hi - lo || Math.abs(hi) || 1;
    const padded = { lo: lo - span * 0.04, hi: hi + span * 0.04 };
    // Round outward to a nice step (1, 2, 5 × 10^k) so axisLabel formatter
    // sees round-thousand values rather than e.g. 71,234.
    const niceStep = (range: number): number => {
      const mag = Math.pow(10, Math.floor(Math.log10(range / 6)));
      const norm = range / 6 / mag;
      const step = norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10;
      return step * mag;
    };
    const step = niceStep(padded.hi - padded.lo);
    return {
      min: Math.floor(padded.lo / step) * step,
      max: Math.ceil(padded.hi / step) * step,
    };
  }, [series, xAxis, spotAtCalc]);

  // Copy CSV — serialise the entire ladder
  const onCopy = () => {
    if (!cached) return;
    const cols = Object.keys(cached) as Array<keyof typeof cached>;
    const colsStr = cols.map((c) => String(c));
    const n = (cached[cols[0] as keyof typeof cached] as number[] | undefined)?.length ?? 0;
    const rows = [colsStr.join(',')];
    for (let i = 0; i < n; i++) {
      rows.push(
        cols
          .map((c) => (cached[c as keyof typeof cached] as number[] | undefined)?.[i] ?? '')
          .join(','),
      );
    }
    try {
      navigator.clipboard?.writeText(rows.join('\n'));
    } catch {
      /* noop */
    }
  };

  const modelBadge = (() => {
    if (!inst) return '';
    if (inst.model === 'hybrid_lv_sv') {
      return `${inst.model} (${(inst.mixing ?? 0).toFixed(2)})`;
    }
    return inst.model;
  })();

  return (
    <Dialog
      open={open}
      onClose={closeLadder}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: 1024,
          backgroundImage: 'none',
          // Standard Diletta navy — same shade as the MuiMenu list and the
          // DatePicker popover; reads as a native panel rather than the
          // default MUI Paper grey.
          backgroundColor: '#002649',
          border: '1px solid',
          borderColor: 'divider',
          // Apply the drag offset via transform so the modal can be moved.
          // Disable transitions on the transform to avoid smoothing during drag.
          transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)`,
          transition: 'none',
        },
      }}
    >
      <DialogTitle
        onPointerDown={onTitlePointerDown}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 1.25,
          px: 2,
          borderBottom: 1,
          borderColor: 'divider',
          cursor: dragStartRef.current ? 'grabbing' : 'grab',
          userSelect: 'none',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Typography
            sx={{ color: brandColour, fontWeight: 700, fontSize: 14, letterSpacing: '0.04em' }}
          >
            {inst?.token ?? ''}
          </Typography>
          <Box sx={{ width: '1px', height: 14, backgroundColor: 'divider' }} />
          <Typography sx={{ fontSize: 12, color: 'text.primary', fontWeight: 500 }}>
            {inst?.contract ?? ''}
          </Typography>
          <Typography
            sx={{
              fontSize: 11,
              color: 'text.secondary',
              fontFamily: 'Inter',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {inst?.expiry ?? ''}
          </Typography>
          <Box
            sx={{
              ml: 0.5,
              px: 0.75,
              py: 0.125,
              borderRadius: '4px',
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'rgba(40, 114, 226, 0.06)',
              fontSize: 9.5,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'text.secondary',
              fontFamily: 'Inter',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {modelBadge}
          </Box>
        </Stack>
        <IconButton onClick={closeLadder} size="small" aria-label="close-risk-ladder">
          <FontAwesomeIcon icon={faXmark} style={{ fontSize: 14 }} />
        </IconButton>
      </DialogTitle>

      {/* Metric strip — flat tabs with a 2px metric-coloured underline on the
          selected one. Avoids the boxed, outlined look of stock MUI toggles. */}
      <Box sx={{ px: 2, pt: 1, pb: 0, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" spacing={0} role="tablist" aria-label="ladder-metric">
          {METRICS.map((m) => {
            const c =
              m === 'PV'
                ? theme.palette.success.main
                : m === 'DELTA'
                  ? greekColour(theme, 'delta')
                  : m === 'GAMMA'
                    ? theme.palette.text.primary
                    : m === 'THETA'
                      ? theme.palette.error.main
                      : greekColour(theme, 'vega');
            const selected = metric === m;
            return (
              <Box
                key={m}
                role="tab"
                aria-selected={selected}
                onClick={() => setMetric(m)}
                sx={{
                  flex: 1,
                  textAlign: 'center',
                  py: 1,
                  cursor: 'pointer',
                  fontSize: 11,
                  fontWeight: selected ? 600 : 500,
                  letterSpacing: '0.08em',
                  color: selected ? c : 'text.secondary',
                  borderBottom: selected ? `2px solid ${c}` : '2px solid transparent',
                  marginBottom: '-1px',
                  transition: 'color 120ms ease',
                  '&:hover': { color: selected ? c : 'text.primary' },
                }}
              >
                {m}
              </Box>
            );
          })}
        </Stack>
      </Box>

      <DialogContent sx={{ minHeight: 420, p: 2 }}>
        {isLoading && !cached && <EmptyState variant="loading" message="Computing ladder…" />}
        {!isLoading && err && !cached && <EmptyState variant="error" message={err} />}
        {!isLoading && !err && !cached && (
          <EmptyState variant="empty" message="No ladder data — run Calculate first." />
        )}
        {cached && yBounds && (
          <Box
            sx={{
              height: 420,
              width: '100%',
              // Cover the global chart watermark (renders at z-index:-1 behind
              // the ECharts canvas) so the plot reads on the same navy as the
              // rest of the modal.
              backgroundColor: '#002649',
              borderRadius: '4px',
            }}
          >
            <Chart
              series={series}
              xAxis={{
                type: 'value',
                // Tight bounds — keep the visible window snug around the data
                // and the current-spot marker.
                min: xBounds?.min,
                max: xBounds?.max,
                axisLabel: {
                  formatter:
                    xAxis === 'shock'
                      ? (v: number) => `${Math.round(v * 100)}%`
                      : (v: number) => fmtSpot(v),
                  fontSize: 10,
                },
                splitLine: { show: true, lineStyle: { opacity: 0.2 } },
              }}
              yAxis={{
                type: 'value',
                // Override the Diletta-wide chartDefaults.yAxis.position='right'.
                // Risk-ladder convention is left-axis (MVP, every TradFi tool).
                position: 'left',
                min: yBounds.min,
                max: yBounds.max,
                axisLabel: {
                  formatter: (v: number) => fmtMetric(metric, v),
                  fontSize: 10,
                  // Right-align labels — MVP RiskLadderChart uses textAnchor="end"
                  // so the rightmost digit of every label sits flush at the
                  // axis edge. Diletta's chartDefaults forces align='center'
                  // which makes mixed-width labels (e.g. "0" vs "-0.030") read
                  // ragged. Override locally.
                  align: 'right',
                  margin: 10,
                },
                splitLine: { show: true, lineStyle: { opacity: 0.2 } },
              }}
              tooltip={{
                trigger: 'axis',
                valueFormatter: (v: unknown) =>
                  typeof v === 'number' ? fmtMetric(metric, v) : String(v ?? ''),
              }}
              grid={{ left: 76, right: 24, top: 16, bottom: 32 }}
            />
          </Box>
        )}
      </DialogContent>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: 'divider' }}
      >
        {/* Left: x-axis toggle */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontSize: 10,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'text.secondary',
            }}
          >
            X-axis
          </Typography>
          <ToggleButtonGroup
            value={xAxis}
            exclusive
            size="small"
            onChange={(_, v) => v && setXAxis(v)}
            aria-label="ladder-x-axis"
          >
            <ToggleButton
              value="spot"
              sx={{ fontSize: 10, py: 0.25, px: 1, textTransform: 'none' }}
            >
              Spot $
            </ToggleButton>
            <ToggleButton
              value="shock"
              sx={{ fontSize: 10, py: 0.25, px: 1, textTransform: 'none' }}
            >
              Shock %
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {/* Middle: spot + metric@spot readout. Two stat cells with a vertical
            divider — same component pattern as the previous top-of-chart row,
            now living in the footer so the chart owns the full DialogContent. */}
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography
              sx={{
                fontSize: 9,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'text.secondary',
                lineHeight: 1.4,
              }}
            >
              Spot
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                fontFamily: 'Inter',
                fontVariantNumeric: 'tabular-nums',
                color: 'text.primary',
                lineHeight: 1.2,
              }}
            >
              {fmtSpot(spotAtCalc)}
            </Typography>
          </Box>
          <Box sx={{ width: '1px', alignSelf: 'stretch', backgroundColor: 'divider', my: 0.25 }} />
          <Box sx={{ textAlign: 'left' }}>
            <Typography
              sx={{
                fontSize: 9,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'text.secondary',
                lineHeight: 1.4,
              }}
            >
              {metric} @ Spot
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                fontFamily: 'Inter',
                fontVariantNumeric: 'tabular-nums',
                color: colourForMetric,
                lineHeight: 1.2,
              }}
            >
              {fmtMetric(metric, valueNow)}
            </Typography>
          </Box>
        </Stack>

        {/* Right: Copy CSV */}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Tooltip title="Copy ladder as CSV">
            <span>
              <Button
                variant="text"
                size="small"
                disabled={!cached}
                onClick={onCopy}
                startIcon={<FontAwesomeIcon icon={faCopy} style={{ fontSize: 10 }} />}
                sx={{ fontSize: 10, textTransform: 'none' }}
              >
                Copy CSV
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Stack>
    </Dialog>
  );
};

export default ExoticsRiskLadderModalContent;
